import { getOpponentCandidates } from './cardModel';
import { evaluate } from './evaluate';

import type { Command, GameState, PassCommand, PlayCommand } from '../../lib';
import {
	action,
	compareRank,
	getCardObjectFromString,
	getCardString,
	getTrickWinner,
	getValidActions,
	makeNextStageCommand,
	makePassCommand,
	makePlayCardCommand,
	Rank,
	RoundStage,
	Suit,
} from '../../lib';

export interface ExpectiminimaxOptions {
	maxDepth?: number;
	timeBudgetMs?: number;
	topK?: number;
	passCandidateCap?: number;
	passSearchDepth?: number;
}

// Wire protocol for running the search on a web worker so the heavy
// expectiminimax computation never blocks the UI thread.
export interface ExpectiminimaxWorkerRequest {
	kind: 'expectiminimax';
	id: number;
	roundStage: RoundStage;
	gameState: GameState;
	position: number;
	options: ExpectiminimaxOptions;
}

export interface ExpectiminimaxWorkerResponse {
	id: number;
	command: PlayCommand | PassCommand | null;
}

const DEFAULTS: Required<ExpectiminimaxOptions> = {
	maxDepth: 4,
	timeBudgetMs: 1000,
	topK: 8,
	passCandidateCap: 16,
	passSearchDepth: 2,
};

interface SearchCtx {
	botPos: number;
	depth: number;
	alpha: number;
	beta: number;
	deadline: number;
	opts: Required<ExpectiminimaxOptions>;
	tt: Map<string, TTEntry>;
}

// Transposition table: caches backed-up values at MAX (bot) nodes keyed by the
// exact game state + remaining depth. Different opponent play-orders reach the
// same bot-to-move state, so caching MAX nodes lets the search explore deeper
// within the time budget. Only MAX nodes are cached — chance-node expectations
// depend on the probability distribution, so caching them is unsafe under
// alpha-beta bounds.
type TTFlag = 'Exact' | 'Lower' | 'Upper';

interface TTEntry {
	value: number;
	flag: TTFlag;
}

// Stable identity for a game state: current player plus every card's location.
// Card locations fully determine future transitions (and the opponent model's
// void/legal-move deductions), so this is a sound key.
function stateKey(state: GameState): string {
	let key = String(state.currentPlayer);

	const cards = [...state.deck].sort((a, b) =>
		getCardString(a) < getCardString(b) ? -1 : getCardString(a) > getCardString(b) ? 1 : 0,
	);

	for (const c of cards) {
		key += '|' + getCardString(c) + ':' + c.pile;
	}

	return key;
}

// Apply a play command and, if it completes the trick, advance the trick.
// Returns the resulting state (next player's turn, or round end / game over).
function step(state: GameState, command: Command): GameState {
	const next = action(command, state);

	if (next.roundStage === RoundStage.TrickEnd) {
		return action(makeNextStageCommand(), next);
	}

	return next;
}

// Order the bot's legal moves best-first so alpha-beta prunes early: plays that
// do not win the current trick come first, then lower ranks.
function orderMoves(moves: PlayCommand[], state: GameState): void {
	const round = state.rounds.at(-1);
	const trick = round?.tricks.at(-1);
	const winningPlayer = trick ? getTrickWinner(trick) : null;
	const winningCard = winningPlayer !== null && trick?.cardsPlayed[winningPlayer]
		? getCardObjectFromString(trick.cardsPlayed[winningPlayer])
		: null;

	moves.sort((a, b) => {
		const ca = getCardObjectFromString(a.data.card);
		const cb = getCardObjectFromString(b.data.card);

		const wa = winningCard
			? ca.suit === winningCard.suit && compareRank(ca.rank, winningCard.rank) > 0
			: false;
		const wb = winningCard
			? cb.suit === winningCard.suit && compareRank(cb.rank, winningCard.rank) > 0
			: false;

		if (wa !== wb) {
			return wa ? 1 : -1;
		}

		return compareRank(ca.rank, cb.rank);
	});
}

// Returns utility = -botPoints (higher is better for the bot).
function search(state: GameState, ctx: SearchCtx): number {
	if (Date.now() > ctx.deadline) {
		return -evaluate(state, ctx.botPos);
	}

	const stage = state.roundStage;

	if (stage === RoundStage.RoundEnd || stage === RoundStage.GameOver) {
		return -evaluate(state, ctx.botPos);
	}

	if (ctx.depth <= 0) {
		return -evaluate(state, ctx.botPos);
	}

	const currentPlayer = state.currentPlayer;

	if (currentPlayer === ctx.botPos) {
		// MAX node: minimize points => maximize utility.
		const moves = getValidActions(state, currentPlayer) as PlayCommand[];

		if (moves.length === 0) {
			return -evaluate(state, ctx.botPos);
		}

		const entryKey = stateKey(state) + '#' + ctx.depth;
		const originalAlpha = ctx.alpha;
		const originalBeta = ctx.beta;
		const hit = ctx.tt.get(entryKey);

		if (hit) {
			if (hit.flag === 'Exact') {
				return hit.value;
			}

			if (hit.flag === 'Lower' && hit.value >= originalBeta) {
				return hit.value;
			}

			if (hit.flag === 'Upper' && hit.value <= originalAlpha) {
				return hit.value;
			}

			if (hit.flag === 'Lower') {
				ctx.alpha = Math.max(ctx.alpha, hit.value);
			} else {
				ctx.beta = Math.min(ctx.beta, hit.value);
			}
		}

		orderMoves(moves, state);

		let best = -Infinity;

		for (const mv of moves) {
			const child = step(state, mv);
			const v = search(child, {
				...ctx,
				depth: ctx.depth - 1,
				alpha: ctx.alpha,
				beta: ctx.beta,
			});

			if (v > best) {
				best = v;
			}

			if (v > ctx.alpha) {
				ctx.alpha = v;
			}

			if (ctx.alpha >= ctx.beta) {
				break;
			}
		}

		let flag: TTFlag = 'Exact';

		if (best <= originalAlpha) {
			flag = 'Upper';
		} else if (best >= originalBeta) {
			flag = 'Lower';
		}

		ctx.tt.set(entryKey, {
			value: best,
			flag,
		});

		return best;
	}

	// CHANCE node: expectation over the opponent's plausible plays.
	const candidates = getOpponentCandidates(state, currentPlayer, ctx.botPos, ctx.opts.topK);

	if (candidates.length === 0) {
		return -evaluate(state, ctx.botPos);
	}

	let expected = 0;
	let cumulative = 0;

	for (const cand of candidates) {
		const cmd = makePlayCardCommand(currentPlayer, getCardString(cand.card));
		const child = step(state, cmd);
		const v = search(child, {
			...ctx,
			depth: ctx.depth - 1, 
		});

		expected += cand.probability * v;
		cumulative += cand.probability;

		if (cumulative > 1 - 1e-6) {
			break;
		}
	}

	return expected;
}

function dangerOf(cards: string[]): number {
	let danger = 0;

	for (const c of cards) {
		const card = getCardObjectFromString(c as never);

		if (card.suit === Suit.Spades && card.rank === Rank.Queen) {
			danger += 13;
		} else if (card.rank === Rank.Ace) {
			danger += 5;
		} else if (card.rank === Rank.King) {
			danger += 3;
		} else if (card.suit === Suit.Hearts) {
			danger += 1;
		}
	}

	return danger;
}

// Build the hypothetical post-pass state: our chosen pass plus a heuristic pass
// for every opponent (each dumps its 3 most dangerous cards), then finish the
// pass so hands are redistributed.
function simulatePostPass(state: GameState, combo: PassCommand, botPos: number): GameState | null {
	let s = action(makePassCommand(botPos, combo.data.cards), state);

	for (let p = 0; p < state.playerCount; ++p) {
		if (p === botPos) {
			continue;
		}

		const hand = s.deck.filter((c) => c.pile === `hand${p}`);

		if (hand.length < 3) {
			continue;
		}

		const oppCards = [...hand]
			.sort((a, b) => dangerOf([getCardString(b)]) - dangerOf([getCardString(a)]))
			.slice(0, 3)
			.map((c) => getCardString(c));

		s = action(makePassCommand(p, oppCards), s);
	}

	if (s.roundStage === RoundStage.PassEnd) {
		s = action(makeNextStageCommand(), s);
	}

	return s;
}

export function chooseTrickPlay(
	state: GameState,
	botPos: number,
	options: ExpectiminimaxOptions = {},
): PlayCommand | null {
	const opts = {
		...DEFAULTS,
		...options, 
	};
	const moves = getValidActions(state, botPos) as PlayCommand[];

	if (moves.length === 0) {
		return null;
	}

	const deadline = Date.now() + opts.timeBudgetMs;
	const tt = new Map<string, TTEntry>();

	orderMoves(moves, state);

	// In the endgame the remaining tree is tiny, so drop the depth cap and play
	// out the rest of the round exactly.
	const cardsLeft = state.deck.filter((c) => c.pile.startsWith('hand')).length;
	const maxDepth = cardsLeft <= 13 ? Math.max(opts.maxDepth, cardsLeft) : opts.maxDepth;

	let best: PlayCommand = moves[0];
	let bestUtil = -Infinity;

	for (const mv of moves) {
		if (Date.now() > deadline) {
			break;
		}

		const child = step(state, mv);
		const v = search(child, {
			botPos,
			depth: maxDepth - 1,
			alpha: bestUtil,
			beta: Infinity,
			deadline,
			opts,
			tt,
		});

		if (v > bestUtil) {
			bestUtil = v;
			best = mv;
		}
	}

	return best;
}

export function choosePass(
	state: GameState,
	botPos: number,
	options: ExpectiminimaxOptions = {},
): PassCommand | null {
	const opts = {
		...DEFAULTS,
		...options, 
	};
	const allCombos = getValidActions(state, botPos) as PassCommand[];

	if (allCombos.length === 0) {
		return null;
	}

	if (allCombos.length === 1) {
		return allCombos[0];
	}

	// Fast pre-rank by danger so we only shallow-search the most promising
	// passes. Lower danger-to-keep is better, so sort by descending danger of
	// the passed cards (we want to pass the most dangerous ones away).
	const ranked = [...allCombos].sort((a, b) => dangerOf(b.data.cards) - dangerOf(a.data.cards));
	const candidates = ranked.slice(0, opts.passCandidateCap);

	const deadline = Date.now() + opts.timeBudgetMs;
	const tt = new Map<string, TTEntry>();

	let best: PassCommand = candidates[0];
	let bestPoints = Infinity;

	for (const combo of candidates) {
		if (Date.now() > deadline) {
			break;
		}

		const postState = simulatePostPass(state, combo, botPos);

		if (!postState) {
			continue;
		}

		const util = search(postState, {
			botPos,
			depth: opts.passSearchDepth,
			alpha: -Infinity,
			beta: Infinity,
			deadline,
			opts,
			tt,
		});

		const points = -util;

		if (points < bestPoints) {
			bestPoints = points;
			best = combo;
		}
	}

	return best;
}

export { DEFAULTS as EXPECTIMINIMAX_DEFAULTS };
